import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { borderRadii, scrollbarCss } from '../../../app/spacing';
import { TextSpan } from '../../../app/typography';
import { useLength } from '../../../hooks/useLength';
import useField from '../../../hooks/useField';
import BaseInput from '../TextInput/BaseInput';
import BaseInputLayout from '../TextInput/BaseInputLayout';
import { hasInputControl, MaybeInputControl } from '../InputControl';
import ResizeWrapper from '../ResizeWrapper';
import { applyRef } from '../props';
import Validation, { useValidation } from '../Validation';
import { gray } from '@kizen/kds/Colors';

const OutlineTextarea = styled(BaseInput)`
  ${scrollbarCss}

  margin-top: 9px;
  height: calc(100% - 23px);
  /* Account for baseline to get accurate spacing */
  margin-bottom: 10px;
  /* Account for right scrollbar width */
  padding-right: 6px;
  padding-left: 10px;
  overflow-y: scroll;
  width: 100%;
  line-height: 1.15;

  resize: none;
`;

export const OutlineLayout = styled(BaseInputLayout)`
  border-radius: ${borderRadii.small};
  border: none;
  // no blue on hover
  ${({ disabled, focused }) =>
    !disabled &&
    !focused &&
    css`
      &:hover {
        border-color: ${gray['gray-500']};
      }
    `}
`;

export const MergeColumn = styled.div`
  width: 100%;
`;

export const CountWrapper = styled.label`
  display: flex;
  justify-content: space-between;
  margin: 12px 0 5px;
`;

export const StyledLabel = styled(TextSpan)`
  line-height: 125%;
  color: ${(props) => (props.disabled ? gray['gray-600'] : gray['gray-800'])};
  margin-bottom: 12px;
`;

// export default function OutlineLongText(props) {
const OutlineLongText = React.forwardRef((props, ref) => {
  const {
    className,
    value: valueProp = '',
    placeholder,
    disabled,
    error,
    onChange,
    onFocus,
    onBlur,
    showCountComponent,
    enableResize,
    inModal,
    setMenuOpen,
    selectionStart,
    ...others
  } = props;
  const { t } = useTranslation();

  const [focused, setFocused] = useState(false);
  const [value, setValue] = useField(valueProp);
  const validationRef = useRef();
  const textareaElem = useRef(null);
  const [, validationProps] = useValidation(validationRef, props);

  const mergeRef = useCallback(
    (el) => {
      applyRef(textareaElem, el);
      applyRef(ref, el);
    },
    [ref]
  );

  const [
    {
      maxLength,
      singleMessageCount,
      maxLengthForSingleMessage,
      countOfMessages,
      maxCountOfMessages,
      totalCount,
    },
    updateEvent,
  ] = useLength();

  useEffect(() => {
    if (!valueProp && value) {
      setValue('');
    }
  }, [valueProp, value, setValue]);

  const textareaChangeEvent = useCallback(
    (text) => {
      if (maxLength >= (text || '').length) {
        if (onChange) {
          onChange(text);
        }
        setValue(text);
        updateEvent(text);
      } else {
        const truncated = text.substring(0, maxLength);
        // TODO Be more polite here than cutting off any overflow
        if (onChange) {
          onChange(truncated);
        }
        setValue(truncated);
        updateEvent(truncated);
      }
    },
    [maxLength, updateEvent, onChange, setValue]
  );

  useEffect(() => {
    if (selectionStart !== null && textareaElem && textareaElem.current) {
      textareaElem.current.selectionStart = selectionStart;
      textareaElem.current.selectionEnd = selectionStart;
      textareaElem.current.focus({ preventScroll: true });
    }
  }, [textareaElem, selectionStart]);

  return (
    <MaybeInputControl variant="outline" forInput {...props}>
      <OutlineLayout
        ref={validationRef}
        className={!hasInputControl(props) && className}
        focused={focused}
        disabled={disabled}
        error={error}
      >
        <MergeColumn>
          <ResizeWrapper
            enableResize={!disabled && enableResize}
            minHeight={63}
            addBorder={!error}
            error={!!error}
          >
            <OutlineTextarea
              ref={mergeRef}
              as="textarea"
              value={value}
              onChange={(ev) => {
                textareaChangeEvent(ev.target.value, ev);
              }}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={(ev) => {
                setFocused(true);
                if (onFocus) onFocus(ev);
              }}
              onBlur={(ev) => {
                setFocused(false);
                if (onBlur) onBlur(ev);
              }}
              enableResize={false}
              {...others}
            />
          </ResizeWrapper>
        </MergeColumn>
      </OutlineLayout>
      <Validation {...validationProps} />
      {showCountComponent && (
        <CountWrapper>
          <StyledLabel>
            {`${singleMessageCount}/${maxLengthForSingleMessage} ${t(
              'characters left in message'
            )} ${countOfMessages} ${t('of')} ${maxCountOfMessages}`}
          </StyledLabel>
          <StyledLabel>
            {`${totalCount} ${t('of')} ${maxLength} ${t('total characters')}`}
          </StyledLabel>
        </CountWrapper>
      )}
    </MaybeInputControl>
  );
});

OutlineLongText.displayName = 'OutlineLongText';

OutlineLongText.defaultProps = {
  value: '',
  onChange: null,
  error: null,
  disabled: null,
  placeholder: null,
  onFocus: null,
  onBlur: null,
  showCountComponent: false,
  enableResize: true,
  inModal: false,
  setMenuOpen: () => {},
  selectionStart: null,
};

export default OutlineLongText;
